import { CommonHcpWebPath } from '@csp/csp-common-routes';

export enum CspPath {
  // General
  LANDING = '/',
  ERROR = '/error/:type',
  PAGE_NOT_FOUND = '/404',
  FALLBACK = '/*',

  // Login/Register
  LOGIN = '/login',
  UNIFY_LOGIN = '/login/unify',
  COGNITO_CALLBACK = '/idp/callback',
  COGNITO_INVITE = '/confirm-invite',
  CONFIRM_ACCOUNT = '/account/confirm',

  // Consents
  CONSENT = '/consent',
  VIEW_CONSENT = '/consent/:consentType',
  EDIT_CONSENT = '/consent/:consentType/edit',
  VIDEO_TRAINING = '/video-training',
  VIDEO_PLAYER = '/video-player',

  // Studies/Study Overview
  STUDIES = '/studies',
  STUDY_OVERVIEW = '/study-overview',
  STUDY_OVERVIEW_OVERVIEW = '/study-overview/overview',
  CLOSEOUT_TRACKER_NOTIFICATION = '/closeout-tracker',

  // Study Documents
  STUDY_OVERVIEW_DOCUMENTS = '/study-overview/documents',
  STUDY_OVERVIEW_DOCUMENTS_FOLDER = '/study-overview/documents/folder/:folderId',
  STUDY_OVERVIEW_DOCUMENTS_TRASH = '/study-overview/documents/trash',
  DOCUMENT_PREVIEW = '/document-preview/:fileId',

  // Study Archiving
  STUDY_OVERVIEW_ARCHIVING = '/study-overview/archiving',
  RELATIVE_STUDY_OVERVIEW_ARCHIVING = 'archiving',
  STUDY_OVERVIEW_ARCHIVING_FILES = '/study-overview/archiving/files',
  STUDY_OVERVIEW_ARCHIVING_SITES = '/study-overview/archiving/sites',
  STUDY_OVERVIEW_ARCHIVING_SITES_EXPORT = '/study-overview/archiving/sites/export',

  // Patient List
  PATIENT_LIST = CommonHcpWebPath.PATIENT_LIST,
  PATIENT_LIST_CLOSEOUT = '/patient/list/closeout',
  PATIENT_LIST_STANDARD = '/patient/list/standard',
  PATIENT_LIST_VISIT = '/patient/list/visit',
  PATIENT_LIST_HBS = '/patient/list/hbs',
  PATIENT_LIST_HBS_DETAILS = '/patient/list/hbs/:sampleId',
  PATIENT_LIST_RPM_EVENTS = '/patient/list/rpm-events',
  PATIENT_LIST_DEBUG = '/patient/list/debug',
  PATIENT_NAV = '/patient/:userId',

  // Patient General
  PATIENT_OVERVIEW = CommonHcpWebPath.PATIENT_OVERVIEW,
  PATIENT_MEDICATION = '/patient/:userId/medication',
  PATIENT_SAMPLES = '/patient/:userId/samples',
  PATIENT_OVERVIEW_PATIENT_STATUS_EDIT = `${CommonHcpWebPath.PATIENT_OVERVIEW}/patient-status/edit`,
  PATIENT_OVERVIEW_MEDICATION_STATUS_EDIT = `${CommonHcpWebPath.PATIENT_OVERVIEW}/medication-status/edit`,
  PATIENT_OVERVIEW_PATIENT_STATUS_DATA_CHANGE = `${CommonHcpWebPath.PATIENT_OVERVIEW}/patient-status/data-change`,
  PATIENT_OVERVIEW_SDS_DATA_CHANGE = `${CommonHcpWebPath.PATIENT_OVERVIEW}/status/:type/data-change`,
  PATIENT_OVERVIEW_SDS_CONFIRM = `${CommonHcpWebPath.PATIENT_OVERVIEW}/status/:type/:value/confirm`,
  PATIENT_OVERVIEW_EDIT_DETAILS = `${CommonHcpWebPath.PATIENT_OVERVIEW}/edit`,
  PATIENT_OVERVIEW_ACTIVITY_DETAILS = `${CommonHcpWebPath.PATIENT_OVERVIEW}/activity`,

  // Patient Questionnaires
  PATIENT_QUESTIONNAIRE_LIST = '/patient/:userId/questionnaires',
  PATIENT_QUESTIONNAIRE_LIST_COMPILE_QUESTIONNAIRE_RESPONSE = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/compile',
  PATIENT_QUESTIONNAIRE_RESPONSE_LIST = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/responses',
  PATIENT_QUESTIONNAIRE_RESPONSE_LIST_SELECTED_RESPONSE = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/responses/:questionnaireResponseId',
  PATIENT_QUESTIONNAIRE_RESPONSE_LIST_EDIT_RESPONSE = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/responses/:questionnaireResponseId/edit',
  PATIENT_QUESTIONNAIRE_RESPONSE_LIST_ADD_MISSED_REASON = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/responses/:questionnaireResponseId/addReason',
  PATIENT_QUESTIONNAIRE_RESPONSE_LIST_COMPILE_QUESTIONNAIRE_RESPONSE = '/patient/:userId/questionnaires/:questionnaireId/:scheduleCode/:requestCode/responses/compile',

  // Patient Devices
  PATIENT_DEVICES = '/patient/:userId/devices',
  PATIENT_DEVICE_OBSERVATION_REDIRECT = '/patient/:userId/device/observation/redirect/:observationId',
  PATIENT_DEVICE_REQUEST_GROUP_DETAILS = '/patient/:userId/device/:groupKey/:requestCode',
  PATIENT_DEVICE_OBSERVATION_DETAILS = '/patient/:userId/device/:groupKey/observation/:scheduleCode/:versionCode/:requestCode/:observationId/:view',
  PATIENT_DEVICE_SESSION_DETAILS = '/patient/:userId/device/:groupKey/session/:scheduleCode/:versionCode/:requestCode/:sessionId',
  PATIENT_DEVICE_LIVE_SESSION = '/patient/:userId/device/live-session/:scheduleCode/:versionCode/:requestCode/:assessmentCode',

  // Patient Visits
  PATIENT_VISITS = '/patient/:userId/visits',
  PATIENT_VISIT_DETAILS = '/patient/:userId/visits/:visitId',
  PATIENT_VISIT_DETAILS_UNBOOKED_SCHEDULED = '/patient/:userId/visits/scheduled/unbooked/:visitUniqueId',

  // Patient Compliance
  PATIENT_COMPLIANCE = CommonHcpWebPath.PATIENT_COMPLIANCE,
  PATIENT_COMPLIANCE_DETAILS = '/patient/:userId/compliance/detailed',

  // Patient RPM
  PATIENT_RPM = '/patient/:userId/rpm/',
  PATIENT_RPM_MODULE_PAGE = CommonHcpWebPath.PATIENT_RPM_MODULE_PAGE,
  PATIENT_RPM_EVENT_DETAILS = CommonHcpWebPath.PATIENT_RPM_EVENT_DETAILS,
  PATIENT_RPM_EVENT_REDIRECT = '/patient/:userId/rpm/event/:eventId',
  PATIENT_HEALTH_EVENT_VIEW = CommonHcpWebPath.PATIENT_HEALTH_EVENT_VIEW,

  // HBS
  HBS = '/hbs',
  HBS_COLLECT_SAMPLES = '/hbs/collect',
  HBS_ALIQUOT_SAMPLES = '/hbs/aliquot',
  HBS_PROCESS_SAMPLES = '/hbs/process',
  HBS_SHIP_SAMPLES = '/hbs/ship',
  HBS_MULTI_ALIQUOT_SAMPLES = '/hbs/multi-aliquot',

  // Team Members
  TEAM_MEMBER_LIST = '/team-members',
  TEAM_MEMBER_NAV = '/team-member/:userId',
  TEAM_MEMBER_OVERVIEW = '/team-member/:userId/overview',

  // Sites
  SITE_LIST = '/sites',
  SITE_LIST_CHANGE_EC_APPROVAL_STATUSES = '/sites/ec-approval',

  SITE_DETAILS = '/sites/:siteId',
  SITE_DETAILS_EDIT_CONTACT_DETAILS = '/sites/:siteId/contact-details/edit',
  SITE_DETAILS_CHANGE_EC_APPROVAL_STATUS = '/sites/:siteId/ec-approval',
  SITE_DETAILS_ADD_ADDITIONAL_LOCATION = '/sites/:siteId/additional-location/add',
  SITE_DETAILS_EDIT_ADDITIONAL_LOCATION = '/sites/:siteId/additional-location/:locationId/edit',
  SITE_DETAILS_REPLACE_ADDITIONAL_LOCATION = '/sites/:siteId/additional-location/:locationId/replace',

  // Reports
  REPORTS = '/reports',
  REPORTS_DATA_CHANGES = '/reports/data-changes',
  REPORTS_DATA_CHANGE_DETAILS = '/reports/data-changes/:dataChangeId',
  REPORTS_STUDY_DATA = '/reports/study-data',
  REPORTS_AUDIT_TRAIL = '/reports/audit-trail',

  // Informed Consents
  INFORMED_CONSENT = '/informed-consent',
  INFORMED_CONSENT_LIST_MASTER = '/informed-consent/master',
  INFORMED_CONSENT_LIST_LOCAL = '/informed-consent/local',
  INFORMED_CONSENT_LIST_SITE = '/informed-consent/site',
  INFORMED_CONSENT_MASTER_ICF_DETAILS = '/informed-consent/master/:icfTypeId',
}

export const DEFAULT_AUTHENTICATED_PATH = CspPath.STUDY_OVERVIEW;
export const UNAUTHENTICATED_PATHS: CspPath[] = [CspPath.LANDING, CspPath.LOGIN, CspPath.UNIFY_LOGIN] as const;
